import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BoardType } from '@/shared/constants/app/routes.constant.ts';
import { boardApi } from './board.api.ts';
import { boardKeys } from './board.keys.ts';
import type { CreatePostInput, Post, UpdatePostInput } from '../types/board.type.ts';

/**
 * 글 생성. 성공 시 해당 게시판 목록 캐시 무효화 → 최신 목록 자동 반영.
 */
export function useCreatePost(type: BoardType) {
  const qc = useQueryClient();
  return useMutation<Post, Error, CreatePostInput>({
    // BoardForm(onError) · BoardDetailView 가 실패를 자체 안내한다.
    meta: { skipGlobalError: true },
    mutationFn: (input) => boardApi.create(type, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: boardKeys.lists(type) });
    },
  });
}

/**
 * 글 수정. 성공 시 목록 + 해당 상세 무효화.
 */
export function useUpdatePost(type: BoardType, id: string | number) {
  const qc = useQueryClient();
  return useMutation<Post, Error, UpdatePostInput>({
    // BoardForm(onError) · BoardDetailView 가 실패를 자체 안내한다.
    meta: { skipGlobalError: true },
    mutationFn: (input) => boardApi.update(type, id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: boardKeys.lists(type) });
      qc.invalidateQueries({ queryKey: boardKeys.detail(type, id) });
    },
  });
}

/**
 * 글 삭제. 성공 시 목록 무효화 + 해당 상세 캐시 제거.
 */
export function useDeletePost(type: BoardType) {
  const qc = useQueryClient();
  return useMutation<void, Error, string | number>({
    // BoardForm(onError) · BoardDetailView 가 실패를 자체 안내한다.
    meta: { skipGlobalError: true },
    mutationFn: (id) => boardApi.remove(type, id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: boardKeys.lists(type) });
      qc.removeQueries({ queryKey: boardKeys.detail(type, id) });
    },
  });
}
